import { AUTOMATION_SEQUENCE_STEP_TYPES } from "./automationSequenceModel";
import { buildDateFromActiveDate } from "./automationTriggerResolver";
import { isManuallyDismissedEvent } from "./automationManualDismissals";

export const AUTO_MUSIC_SEQUENCE_CATCH_UP_MS = 30000;

export const AUTO_MUSIC_AUTOMATION_SOURCE = "automationSequence";
export const AUTO_MUSIC_ROUTINE_OWNERSHIP = "routine";

export const isRoutineOwnedMusicSegment = (segment = {}) =>
  segment?.automationSource === AUTO_MUSIC_ROUTINE_OWNERSHIP
  || segment?.source === AUTO_MUSIC_AUTOMATION_SOURCE
  || Boolean(segment?.sequenceRuntimeEventKey);

export const isStandaloneAutoMusicSegment = (segment = {}) =>
  Boolean(segment?.playlistId) && !isRoutineOwnedMusicSegment(segment);

export const isOpenEndedMusicStopMode = (stopMode = "duration") =>
  stopMode === "manual" || stopMode === "playAll";

export const isOpenEndedMusicEvent = (event = {}) =>
  event?.type === AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC
  && isOpenEndedMusicStopMode(event?.config?.stopMode || "duration");

export const isRecoverableFiredOpenEndedMusicRow = (row = {}, event = {}) => {
  if (row?.status !== "fired" || !isOpenEndedMusicEvent(event) || isUserDismissedMusicLedgerRow(row)) {
    return false;
  }

  const details = row?.details || {};
  const confirmedPlayback =
    details.playbackConfirmed === true ||
    (details.playbackConfirmed == null && details.playbackActive !== false);
  const launchedButNotAudible =
    details.playbackLaunched === true &&
    details.playbackConfirmed !== true &&
    details.playbackActive === false;

  return (confirmedPlayback && details.playbackActive !== false) || launchedButNotAudible;
};

/** Teacher manually closed Auto Music — never recover/relaunch that occurrence. */
export const isUserDismissedMusicLedgerRow = (row = {}) =>
  Boolean(row?.details?.userDismissed)
  || row?.details?.playbackEndedReason === "manual";

export const shouldSuppressMusicForManualDismiss = (event = {}, ledgerRow = null) =>
  isUserDismissedMusicLedgerRow(ledgerRow)
  || isManuallyDismissedEvent(event);

export const buildRoutineMusicSegmentMetadata = (event = {}) => ({
  source: AUTO_MUSIC_AUTOMATION_SOURCE,
  automationSource: AUTO_MUSIC_ROUTINE_OWNERSHIP,
  sequenceId: event.sequenceId,
  sequenceStepId: event.stepId,
  sequenceRuntimeEventKey: event.key,
  eventKey: event.key,
  relativeSourceClassId: event.classId,
  relativeSourceClassName: event.className,
  anchorTime: event.anchorTime,
});

export const resolveRoutineMusicOverlapAction = ({
  hasActiveSegment = false,
  activeSegment = null,
  incomingSegment = null,
} = {}) => {
  if (!hasActiveSegment) {
    return { action: "start" };
  }

  if (!isRoutineOwnedMusicSegment(incomingSegment)) {
    return { action: "skip", reason: "MUSIC_ALREADY_PLAYING" };
  }

  if (isRoutineOwnedMusicSegment(activeSegment)) {
    return { action: "replace" };
  }

  return { action: "skip", reason: "MUSIC_ALREADY_PLAYING" };
};

export const getEndOfActiveDateMs = (activeDateInput = new Date()) => {
  const date = buildDateFromActiveDate(activeDateInput);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  ).getTime();
};

export const getMusicNeverStartedCatchUpEndMs = (
  event = {},
  recentDueMs = AUTO_MUSIC_SEQUENCE_CATCH_UP_MS
) => {
  const stopMode = event?.config?.stopMode || "duration";
  const startMs = event?.startTime?.getTime?.();
  if (!Number.isFinite(startMs)) {
    return NaN;
  }

  if (stopMode === "duration") {
    const endMs = event?.endTime?.getTime?.();
    if (Number.isFinite(endMs)) {
      return endMs;
    }
    return startMs + Math.max(1, Number(event.durationSeconds) || 120) * 1000;
  }

  return startMs + recentDueMs;
};

export const getMusicRecoveryWindowEndMs = (
  event = {},
  activeDate,
  ledgerRow = {},
  recentDueMs = AUTO_MUSIC_SEQUENCE_CATCH_UP_MS
) => {
  const hasStarted = ["fired", "recovered"].includes(ledgerRow?.status);
  const stopMode = event?.config?.stopMode || "duration";

  if (!hasStarted) {
    return getMusicNeverStartedCatchUpEndMs(event, recentDueMs);
  }

  if (stopMode === "duration") {
    const endMs = event?.endTime?.getTime?.();
    if (Number.isFinite(endMs)) {
      return endMs;
    }
    const startMs = event?.startTime?.getTime?.();
    if (Number.isFinite(startMs)) {
      return startMs + Math.max(1, Number(event.durationSeconds) || 120) * 1000;
    }
    return NaN;
  }

  return getEndOfActiveDateMs(activeDate);
};

export const getMusicRuntimeEventEndTime = ({
  step = {},
  startTime,
  durationSeconds,
  activeDate,
}) => {
  const stopMode = step?.config?.stopMode || "duration";
  if (stopMode === "manual" || stopMode === "playAll") {
    return new Date(getEndOfActiveDateMs(activeDate));
  }

  return new Date(startTime.getTime() + Math.max(1, durationSeconds) * 1000);
};

/** Don't flash-launch longer Duration music with only a few seconds left. */
export const MUSIC_DURATION_MIN_REMAINING_SECONDS = 30;

export const getConfiguredMusicDurationSeconds = (event = {}) => {
  const configured = Number(event?.durationSeconds);
  const configDuration = Number(event?.config?.duration);
  return Math.max(
    1,
    (Number.isFinite(configured) && configured > 0 ? configured : 0)
      || (Number.isFinite(configDuration) && configDuration > 0 ? configDuration : 0)
      || 120
  );
};

/**
 * Seconds left until the original intended end for Duration-mode music.
 * Manual / Play All have no hard end for this helper (returns configured length for display).
 * May be 0 or negative when the scheduled window has already ended.
 */
export const getMusicPlaybackDurationSeconds = (event = {}, nowInput = new Date()) => {
  const stopMode = event?.config?.stopMode || "duration";
  const fallback = getConfiguredMusicDurationSeconds(event);

  if (isOpenEndedMusicStopMode(stopMode)) {
    return fallback;
  }

  const now = nowInput instanceof Date ? nowInput : new Date(nowInput);
  const endMs = event?.endTime?.getTime?.();
  if (Number.isFinite(endMs)) {
    return Math.ceil((endMs - now.getTime()) / 1000);
  }

  return fallback;
};

export const shouldLaunchDurationMusic = (event = {}, nowInput = new Date()) => {
  const stopMode = event?.config?.stopMode || "duration";
  if (isOpenEndedMusicStopMode(stopMode)) {
    return { ok: true, remainingSeconds: null };
  }

  const remainingSeconds = getMusicPlaybackDurationSeconds(event, nowInput);
  const minRemainingSeconds = Math.min(
    MUSIC_DURATION_MIN_REMAINING_SECONDS,
    getConfiguredMusicDurationSeconds(event)
  );
  if (remainingSeconds < minRemainingSeconds) {
    return { ok: false, remainingSeconds };
  }

  return { ok: true, remainingSeconds };
};
